import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
/* -----------------Globals--------------- */
import { ScrollView } from "react-native";

/* -----------------Helpers & Hooks--------------- */
import useSessionConfig, {
	type AdditionalParams,
} from "../hooks/useSessionConfig";

/* -----------------Types--------------- */
import type { Distribution, PracticeTestExam } from "@memorang/types";

import { diagnosticTagIds } from "@helpers/session";
import { smartPluralize } from "@helpers/smartPluralize";
import { useDevice } from "@hooks/useDevice";
/* -----------------Child components--------------- */
import { Box } from "@memorang/ui";
import { StickyButtonContainer } from "@memorang/ui";
import type { DiagnosticItem } from "api";
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";
import TagFiltersList from "../components/TagFiltersList";
import { estimateSessionTime } from "../helpers/timeEstimation";
import useLoadingMessages from "../hooks/useLoadingMessages";
import type { SetsAndItemsType } from "../types/tag";

type Props = {
	available: Distribution;
	additionalParams?: AdditionalParams;
	handleCreateSession: (distribution: Distribution, tags: number[]) => void;
	creatingSession?: boolean;
	handleShowPurchase: () => void;
	handleClose?: () => void;
	hasAccess?: boolean;
	diagnosticItems?: DiagnosticItem[];
	setsAndItems: SetsAndItemsType;
	exams?: PracticeTestExam[];
};

const _getSessionInfoText = ({
	tagIds,
	setsAndItems,
	t,
}: {
	tagIds: number[];
	setsAndItems: SetsAndItemsType;
	t: TFunction;
}) => {
	const numSets = tagIds.reduce((acc, tagId) => {
		return (
			acc + setsAndItems[tagId as keyof typeof setsAndItems]?.forEstimateSets
		);
	}, 0);
	const numItems = tagIds.reduce((acc, tagId) => {
		return (
			acc + setsAndItems[tagId as keyof typeof setsAndItems]?.forEstimateItems
		);
	}, 0);
	const estimatedMinutes = estimateSessionTime({
		tagIds: tagIds,
		setsAndItems: setsAndItems,
	});

	const hours = Math.floor(estimatedMinutes / 60);
	const minutes = estimatedMinutes % 60;

	let timeString = "";
	const timeCount = hours > 0 ? hours : minutes;

	if (hours > 0) {
		timeString = `${hours} ${smartPluralize("hour", hours)}`;
		if (minutes > 0) {
			timeString = `${timeString} ${minutes} ${smartPluralize("minute", minutes)}`;
		}
	} else {
		timeString = `${minutes} ${smartPluralize("minute", minutes)}`;
	}

	return t("sessionConfig.infoText", {
		numItems,
		numSets,
		timeString,
		itemCount: `${smartPluralize("item", numItems)}`,
		setCount: `${smartPluralize("set", numSets)}`,
		timeCount,
	});
};

const SessionConfig = ({
	available,
	additionalParams,
	handleCreateSession,
	creatingSession,
	handleShowPurchase,
	hasAccess,
	setsAndItems,
	exams,
	handleClose,
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const {
		handleSetTagCheckboxMap,
		selectedTagCheckboxMap,
		numRequiredItems,
		requiredDistribution,
	} = useSessionConfig(available, additionalParams || {}, creatingSession);

	const ctaText = useLoadingMessages(Boolean(creatingSession));

	const _selectedTagIds = selectedTagCheckboxMap
		? Object.keys(selectedTagCheckboxMap).filter(
				(value) => selectedTagCheckboxMap[value],
			)
		: [];

	const filterSelectedDiagnosticTags =
		selectedTagCheckboxMap &&
		Object.keys(selectedTagCheckboxMap).filter(
			(value) =>
				selectedTagCheckboxMap[value] &&
				Object.values(diagnosticTagIds).includes(Number(value)),
		);

	const isDiagnosticTagSelected =
		filterSelectedDiagnosticTags && filterSelectedDiagnosticTags.length > 0;

	const handleStartSession = () => {
		const selectedTags = selectedTagCheckboxMap
			? Object.keys(selectedTagCheckboxMap).filter(
					(tagId) => selectedTagCheckboxMap[tagId],
				)
			: [];
		if (!creatingSession) {
			handleCreateSession(requiredDistribution, selectedTags.map(Number));
		}
	};

	// const infoAlertText = getSessionInfoText({
	// 	tagIds: selectedTagIds.map(Number),
	// 	setsAndItems: setsAndItems,
	// 	t,
	// });

	const selectedTags = selectedTagCheckboxMap
		? Object.keys(selectedTagCheckboxMap).filter(
				(value) => selectedTagCheckboxMap[value],
			)
		: [];

	return (
		<StickyButtonContainer
			buttonText={ctaText}
			icon={creatingSession ? undefined : t("sessionConfig.playIcon")}
			onPress={handleStartSession}
			loading={creatingSession}
			paddingHorizontal={isMobile ? 16 : 24}
			showButton={hasAccess}
			disabled={
				!numRequiredItems || selectedTags.length === 0 || creatingSession
			}
		>
			<ScrollView
				contentContainerStyle={{
					paddingVertical: 16,
					paddingHorizontal: isMobile ? 0 : 8,
				}}
			>
				<Box gap={16} flex={1}>
					<DiagnosticItemsContainer
						exams={exams}
						hasAccess={hasAccess}
						handleClose={handleClose}
						handleShowPurchase={handleShowPurchase}
						paddingHorizontal={16}
						isSessionConfig
						disableAll={creatingSession}
					/>
					{additionalParams?.sectionTags ? (
						<TagFiltersList
							disableAll={creatingSession || isDiagnosticTagSelected}
							selectedCheckboxMap={selectedTagCheckboxMap}
							setsAndItems={setsAndItems}
							handleSelectCheckbox={(distKey) => {
								if (hasAccess) {
									handleSetTagCheckboxMap(distKey);
								} else {
									handleShowPurchase();
								}
							}}
							hasAccess={hasAccess}
							showPremiumChip={!hasAccess}
							sectionTags={additionalParams.sectionTags}
						/>
					) : null}
				</Box>
			</ScrollView>
			{/* {hasAccess && (
				<Box padding={16}>
					<InfoAlert text={infoAlertText} fontSize={16} />
				</Box>
			)} */}
		</StickyButtonContainer>
	);
};

export default SessionConfig;
